import {
  ArrowLeft,
  ArrowRight,
  Check,
  Eye,
  EyeOff,
  LockKeyhole,
  ShieldCheck,
  UserRound,
  UsersRound,
  MessageSquareText,
  LoaderCircle,
} from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../api/client";
import type { UserSummary } from "../api/models";
import { emptyUser, normalizeUser } from "../api/user";
import { BrandMark } from "../components/BrandMark";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useCopy } from "../i18n/copy";
import { useSession } from "../store/session";
import { configApi } from "../api/config";
import {
  loadLoginCredentials,
  saveLoginCredentials,
} from "../utils/credentialVault";
import {
  isValidInternationalPhone,
  isValidLoginPhone,
  normalizeInternationalPhone,
} from "../utils/phone";

interface LoginResponse {
  auth?: string;
  token?: string;
  access_token?: string;
  user?: Record<string, unknown>;
}

export function AuthPage({ mode }: { mode: "login" | "register" }) {
  const isRegister = mode === "register";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitedBy = searchParams.get("code")?.trim() || "";
  const setSession = useSession((state) => state.setSession);
  const { tr } = useCopy();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [codeSeconds, setCodeSeconds] = useState(0);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    phone: "",
    username: "",
    pass: "",
    fundPass: "",
    inviteCode: invitedBy,
    verifyCode: "",
  });
  const registerConfig = useQuery({
    queryKey: ["register-config"],
    queryFn: configApi.register,
    enabled: isRegister,
    staleTime: 300000,
  });
  const sendCode = useMutation({
    mutationFn: () => {
      if (!isValidInternationalPhone(form.phone))
        throw new ApiError(tr("Ingresa un número de celular válido."));
      return configApi.sendRegistrationCode(
        normalizeInternationalPhone(form.phone),
      );
    },
    onSuccess: () => setCodeSeconds(60),
  });
  useEffect(() => {
    if (isRegister) return;
    let active = true;
    void loadLoginCredentials().then((credentials) => {
      if (!active || !credentials) return;
      setForm((current) => ({
        ...current,
        phone: current.phone || credentials.phone,
        pass: current.pass || credentials.pass,
      }));
    });
    return () => {
      active = false;
    };
  }, [isRegister]);
  useEffect(() => {
    if (codeSeconds <= 0) return;
    const timer = window.setTimeout(
      () => setCodeSeconds((current) => Math.max(0, current - 1)),
      1000,
    );
    return () => window.clearTimeout(timer);
  }, [codeSeconds]);
  const update = (field: keyof typeof form, value: string) =>
    setForm((current) => ({ ...current, [field]: value }));

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      const isPhoneValid = isRegister
        ? isValidInternationalPhone(form.phone)
        : isValidLoginPhone(form.phone);
      if (!isPhoneValid)
        throw new ApiError(tr("Ingresa un número de celular válido."));
      const normalizedPhone = normalizeInternationalPhone(form.phone);
      if (isRegister) {
        if (registerConfig.data?.isOpenPhoneVerify && !form.verifyCode.trim())
          throw new ApiError(tr("Ingresa el código de verificación."));
        if (registerConfig.data?.isInviteNecessary && !form.inviteCode.trim())
          throw new ApiError(tr("Ingresa el código de invitación."));
        await api.post("/api/v1/register", {
          ...form,
          phone: normalizedPhone,
          againPass: form.pass,
        });
        await saveLoginCredentials({
          phone: normalizedPhone,
          pass: form.pass,
        }).catch(() => undefined);
        navigate("/auth/login");
      } else {
        const login = await api.post<LoginResponse>("/api/v1/login", {
          phone: normalizedPhone,
          pass: form.pass,
        });
        const token = login.auth || login.token || login.access_token;
        if (token) localStorage.setItem("AUTH-TOKEN", token);
        let user = login.user ? normalizeUser(login.user) : emptyUser;
        try {
          user = normalizeUser(
            await api.post<Record<string, unknown>>("/api/v2/get/user"),
          );
        } catch {
          user = normalizeUser(login.user || {});
        }
        setSession(user, token);
        await saveLoginCredentials({
          phone: normalizedPhone,
          pass: form.pass,
        }).catch(() => undefined);
        navigate("/");
      }
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : tr("No pudimos completar la solicitud. Revisa tus datos."),
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="auth-page">
      <section className="auth-brand-panel">
        <div className="auth-brand-content">
          <BrandMark />
          <p className="eyebrow light">{tr("Operación digital")}</p>
          <h1>
            {tr("Capacidad que se entiende.")}
            <br />
            {tr("Progreso que se puede seguir.")}
          </h1>
        </div>
        <div className="auth-landscape" aria-hidden="true">
          <i />
          <i />
          <i />
          <span />
        </div>
      </section>
      <section className="auth-form-panel">
        <div className="auth-form-wrap">
          <div className="auth-utility">
            <Link to="/" className="back-link">
              <ArrowLeft /> {tr("Inicio")}
            </Link>
            <LanguageSwitcher />
          </div>
          <p className="eyebrow">
            {tr(isRegister ? "Crear una cuenta" : "Bienvenido de nuevo")}
          </p>
          <h2>
            {tr(isRegister ? "Empieza tu recorrido" : "Ingresa a tu espacio")}
          </h2>
          <form onSubmit={submit}>
            {isRegister && (
              <label>
                {tr("Nombre de usuario")}
                <span className="input-shell">
                  <UserRound />
                  <input
                    value={form.username}
                    onChange={(event) => update("username", event.target.value)}
                    autoComplete="username"
                    required
                    placeholder="Ej. MariaF"
                  />
                </span>
              </label>
            )}
            <label>
              {tr("Número de celular")}
              <span className={isRegister ? "phone-shell" : "input-shell"}>
                {isRegister && <b>+593</b>}
                <input
                  value={form.phone}
                  onChange={(event) => update("phone", event.target.value)}
                  inputMode="tel"
                  autoComplete="tel"
                  required
                  placeholder={
                    isRegister ? "99 123 4567 / +1 202 555 0123" : tr("Número registrado")
                  }
                />
              </span>
              <small>
                {tr(
                  isRegister
                    ? "Aceptamos números móviles de cualquier país; Ecuador se muestra como código de referencia."
                    : "Ingresa tu número sin seleccionar un código de país.",
                )}
              </small>
            </label>
            <label>
              {tr("Contraseña de acceso")}
              <span className="input-shell">
                <LockKeyhole />
                <input
                  type={showPassword ? "text" : "password"}
                  value={form.pass}
                  onChange={(event) => update("pass", event.target.value)}
                  autoComplete={
                    isRegister ? "new-password" : "current-password"
                  }
                  minLength={6}
                  required
                  placeholder={tr("Mínimo 6 caracteres")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={tr(
                    showPassword ? "Ocultar contraseña" : "Mostrar contraseña",
                  )}
                >
                  {showPassword ? <EyeOff /> : <Eye />}
                </button>
              </span>
            </label>
            {isRegister && (
              <>
                <label>
                  {tr("Contraseña de fondos")}
                  <span className="input-shell">
                    <ShieldCheck />
                    <input
                      type="password"
                      value={form.fundPass}
                      onChange={(event) =>
                        update("fundPass", event.target.value)
                      }
                      required
                      minLength={6}
                      placeholder={tr("Protege tus operaciones")}
                    />
                  </span>
                </label>
                <label>
                  {tr("Código de invitación")}{" "}
                  {!registerConfig.data?.isInviteNecessary && (
                    <em>{tr("Opcional")}</em>
                  )}
                  <span className="input-shell">
                    <UsersRound />
                    <input
                      value={form.inviteCode}
                      disabled={Boolean(invitedBy)}
                      onChange={(event) =>
                        update("inviteCode", event.target.value)
                      }
                      required={registerConfig.data?.isInviteNecessary}
                      placeholder={tr("Código")}
                    />
                  </span>
                </label>
                {registerConfig.data?.isOpenPhoneVerify && (
                  <label>
                    {tr("Código de verificación")}
                    <span className="verification-row">
                      <span className="input-shell">
                        <MessageSquareText />
                        <input
                          value={form.verifyCode}
                          onChange={(event) =>
                            update("verifyCode", event.target.value)
                          }
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          required
                          placeholder={tr("Código")}
                        />
                      </span>
                      <button
                        type="button"
                        className="button secondary verification-send"
                        disabled={
                          !isValidInternationalPhone(form.phone) ||
                          codeSeconds > 0 ||
                          sendCode.isPending
                        }
                        onClick={() => sendCode.mutate()}
                      >
                        {sendCode.isPending ? (
                          <LoaderCircle className="spin" />
                        ) : codeSeconds > 0 ? (
                          String(codeSeconds) + "s"
                        ) : (
                          tr("Enviar código")
                        )}
                      </button>
                    </span>
                    {sendCode.isSuccess && (
                      <small className="success-text">
                        {tr("Código enviado.")}
                      </small>
                    )}
                    {sendCode.isError && (
                      <small className="error-text">
                        {sendCode.error.message}
                      </small>
                    )}
                  </label>
                )}
              </>
            )}
            {isRegister && registerConfig.isError && (
              <div className="form-error" role="alert">
                {tr("No pudimos cargar la configuración de registro.")}
              </div>
            )}
            {error && (
              <div className="form-error" role="alert">
                {error}
              </div>
            )}
            <button
              className="button primary full auth-submit"
              disabled={
                loading ||
                (isRegister &&
                  (registerConfig.isLoading || registerConfig.isError))
              }
            >
              {tr(
                loading
                  ? "Procesando…"
                  : isRegister
                    ? "Crear cuenta"
                    : "Ingresar",
              )}{" "}
              {!loading && <ArrowRight />}
            </button>
          </form>
          <p className="auth-switch">
            {tr(
              isRegister
                ? "¿Ya tienes una cuenta?"
                : "¿Todavía no tienes una cuenta?",
            )}{" "}
            <Link to={isRegister ? "/auth/login" : "/auth/register"}>
              {tr(isRegister ? "Iniciar sesión" : "Registrarme")}
            </Link>
          </p>
        </div>
      </section>
    </main>
  );
}
